import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Grid } from '@mui/material'
import { useTheme } from '@mui/styles'
import SettingsMenuLayout from 'components/layouts/SettingsMenuLayout'
import NavTabs from 'components/generic/tabs/NavTabs'
import TabsLayoutContext from 'components/layouts/TabsLayoutContextProvider'
import { AppTab } from 'AppRoutes'

interface TabsLayoutProps {
  tabs?: AppTab[]
  tabOrientation?: 'horizontal' | 'vertical'
  contentClassName?: string
  isSideMenu?: boolean
  isSettings?: boolean
}

const TabsLayout: React.FC<TabsLayoutProps> = ({ tabs, tabOrientation, contentClassName, isSideMenu, isSettings }) => {
  const theme = useTheme()
  const tabsRef = useRef(tabs)
  const [pageTabs, setPageTabs] = useState(tabsRef.current)
  const [tabsVisibility, setTabsVisibility] = useState(!!tabs)
  const location = useLocation()

  const setContainerTabs = customTabs => {
    setPageTabs(customTabs)
    tabsRef.current = customTabs
  }
  useEffect(() => {
    setPageTabs(tabsRef.current)
  }, [tabsRef.current])

  const flexDirection = tabOrientation === 'vertical' ? 'row' : 'column'

  const borderProps = tabOrientation === 'vertical' ? { borderRight: 1 } : { borderBottom: 1 }

  const setContainerTabsVisibility = useCallback(
    customTabsVisibility => {
      setTabsVisibility(customTabsVisibility)
    },
    [tabsVisibility],
  )

  const shouldShowTabs = () => {
    const hasIdParam =
      location.pathname.match(/\/:[^/]+/) ||
      location.pathname.includes('/customers/') ||
      location.pathname.includes('/trucks/') ||
      location.pathname.includes('/trailers/') ||
      location.pathname.includes('/containers/') ||
      location.pathname.includes('/app/settings')
    return (
      !isSettings &&
      (isSideMenu || tabsVisibility) &&
      (window.localStorage.getItem('useCategoryNav') === 'false' || hasIdParam)
    )
  }

  return (
    <TabsLayoutContext.Provider
      value={{
        setContainerTabsVisibility,
        setContainerTabs,
      }}
    >
      <Grid container item flexDirection={flexDirection} sx={{ flexGrow: 1, minHeight: 0 }} wrap="nowrap">
        {isSettings ? (
          <Grid
            id="settings-side-bar"
            item
            {...borderProps}
            sx={{
              borderColor: 'divider',
              background: (theme as any).palette.background.settingsMenu,
            }}
            component="nav"
          >
            <SettingsMenuLayout />
          </Grid>
        ) : null}

        {shouldShowTabs() ? (
          <Grid
            id={isSideMenu ? 'nav-side-bar' : 'nav-tabs-bar'}
            item
            {...borderProps}
            sx={{
              borderColor: 'divider',
              position: isSideMenu ? 'unset' : 'sticky',
              top: isSideMenu ? 'unset' : 73,
              background: theme.palette.background.default,
              'z-index': isSideMenu ? 'unset' : 2,
            }}
            component="nav"
          >
            <NavTabs tabs={pageTabs} orientation={tabOrientation} isSideMenu={isSideMenu} />
          </Grid>
        ) : null}

        <Grid item sx={{ flexGrow: 1, minHeight: 0 }} className={contentClassName}>
          <Outlet />
        </Grid>
      </Grid>
    </TabsLayoutContext.Provider>
  )
}

TabsLayout.defaultProps = {
  tabs: [
    {
      title: '',
      route: '',
      matchPath: '',
    },
  ],
  tabOrientation: 'horizontal',
  contentClassName: '',
  isSideMenu: false,
  isSettings: false,
}

export default TabsLayout
